import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  Chip,
  Button,
  CircularProgress,
  Grid,
  TextField,
  MenuItem,
  Tabs,
  Tab
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Telegram as TelegramIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { ru } from 'date-fns/locale';
import axios from 'axios';

const AdvertisementSendResults = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [advertisement, setAdvertisement] = useState(null);
  const [sentItems, setSentItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  useEffect(() => {
    fetchAdvertisement();
    fetchSentItems();
  }, [id, tabValue, page, rowsPerPage, statusFilter, searchQuery]);

  const fetchAdvertisement = async () => {
    try {
      const response = await axios.get(`/api/advertisements/${id}`);
      setAdvertisement(response.data);
    } catch (err) {
      console.error('Error fetching advertisement:', err);
      setError('Ошибка при загрузке рекламного материала');
    }
  };

  const fetchSentItems = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const recipientType = tabValue === 0 ? 'email' : 'telegram';
      const status = statusFilter !== 'all' ? statusFilter : undefined;
      
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        recipientType,
        status,
        search: searchQuery || undefined
      };
      
      const response = await axios.get(`/api/advertisements/${id}/sent`, { params });

      const data = response.data || {};
      const items = data.sentItems || [];
      setSentItems(items);

      // Safely determine total count with fallbacks for different response shapes
      const totalFromPagination = data.pagination && typeof data.pagination.total === 'number'
        ? data.pagination.total
        : undefined;
      const totalFromRoot = typeof data.total === 'number' ? data.total : undefined;
      const computedTotal = totalFromPagination ?? totalFromRoot ?? items.length;

      setTotalCount(computedTotal);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching sent items:', err);
      setError('Ошибка при загрузке результатов отправки');
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setPage(0);
    setStatusFilter('all');
    setSearchQuery('');
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
    setPage(0);
  };

  const handleSearchQueryChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Нет данных';
    
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true, locale: ru });
    } catch (error) {
      return dateString;
    }
  };

  const getStatusChip = (status) => {
    switch (status) {
      case 'sent':
        return <Chip icon={<ScheduleIcon fontSize="small" />} label="Отправлено" color="info" size="small" />;
      case 'delivered':
        return <Chip icon={<CheckCircleIcon fontSize="small" />} label="Доставлено" color="success" size="small" />;
      case 'opened':
        return <Chip icon={<CheckCircleIcon fontSize="small" />} label="Открыто" color="success" size="small" />;
      case 'clicked':
        return <Chip icon={<CheckCircleIcon fontSize="small" />} label="Переход по ссылке" color="success" size="small" />;
      case 'failed':
        return <Chip icon={<ErrorIcon fontSize="small" />} label="Ошибка" color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  const renderEmailSentTable = () => {
    return (
      <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
        <Table stickyHeader aria-label="sticky table" size="small">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Имя</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Отправлено</TableCell>
              <TableCell>Ошибка</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress size={24} sx={{ my: 1 }} />
                </TableCell>
              </TableRow>
            ) : sentItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Результаты отправки не найдены
                </TableCell>
              </TableRow>
            ) : (
              sentItems.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>{item.recipientIdentifier}</TableCell>
                  <TableCell>{item.emailSubscriber?.name || 'Не указано'}</TableCell>
                  <TableCell>{getStatusChip(item.status)}</TableCell>
                  <TableCell>{formatDate(item.sentAt)}</TableCell>
                  <TableCell>
                    {item.errorMessage ? (
                      <Typography variant="body2" color="error" noWrap sx={{ maxWidth: 300 }}>
                        {item.errorMessage}
                      </Typography>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const renderTelegramSentTable = () => {
    return (
      <TableContainer sx={{ maxHeight: 'calc(100vh - 300px)' }}>
        <Table stickyHeader aria-label="sticky table" size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID чата</TableCell>
              <TableCell>Имя пользователя</TableCell>
              <TableCell>Имя</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Отправлено</TableCell>
              <TableCell>Ошибка</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={24} sx={{ my: 1 }} />
                </TableCell>
              </TableRow>
            ) : sentItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  Результаты отправки не найдены
                </TableCell>
              </TableRow>
            ) : (
              sentItems.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>{item.recipientIdentifier}</TableCell>
                  <TableCell>{item.telegramSubscriber?.username || 'Не указано'}</TableCell>
                  <TableCell>
                    {item.telegramSubscriber ? 
                      `${item.telegramSubscriber.firstName || ''} ${item.telegramSubscriber.lastName || ''}`.trim() : 
                      'Не указано'
                    }
                  </TableCell>
                  <TableCell>{getStatusChip(item.status)}</TableCell>
                  <TableCell>{formatDate(item.sentAt)}</TableCell>
                  <TableCell>
                    {item.errorMessage ? (
                      <Typography variant="body2" color="error" noWrap sx={{ maxWidth: 300 }}>
                        {item.errorMessage}
                      </Typography>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Результаты отправки рекламы
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            component={Link}
            to="/admin/advertisements"
          >
            Назад к списку
          </Button>
        </Box>

        {advertisement && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              {advertisement.title}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Статус:
                </Typography>
                <Typography variant="body1">
                  {advertisement.status === 'draft' ? 'Черновик' : 
                   advertisement.status === 'ready' ? 'Готов к отправке' : 
                   advertisement.status === 'sent' ? 'Отправлен' : advertisement.status}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Последняя отправка:
                </Typography>
                <Typography variant="body1">
                  {advertisement.lastSentAt ? formatDate(advertisement.lastSentAt) : 'Не отправлялся'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Тема письма:
                </Typography>
                <Typography variant="body1">
                  {advertisement.emailSubject || 'Не указана'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  Автор:
                </Typography>
                <Typography variant="body1">
                  {advertisement.author ? 
                    advertisement.author.name : 
                    'Не указан'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        )}

        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          sx={{ mb: 3 }}
          variant="fullWidth"
        >
          <Tab 
            icon={<EmailIcon />} 
            iconPosition="start" 
            label="Email отправки" 
          />
          <Tab 
            icon={<TelegramIcon />} 
            iconPosition="start" 
            label="Telegram отправки" 
          />
        </Tabs>

        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                label="Поиск"
                variant="outlined"
                fullWidth
                size="small"
                value={searchQuery}
                onChange={handleSearchQueryChange}
                placeholder={tabValue === 0 ? "Поиск по email или имени" : "Поиск по имени или ID"}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                select
                label="Статус"
                variant="outlined"
                fullWidth
                size="small"
                value={statusFilter}
                onChange={handleStatusFilterChange}
              >
                <MenuItem value="all">Все статусы</MenuItem>
                <MenuItem value="sent">Отправлено</MenuItem>
                <MenuItem value="delivered">Доставлено</MenuItem>
                <MenuItem value="opened">Открыто</MenuItem>
                <MenuItem value="clicked">Переход по ссылке</MenuItem>
                <MenuItem value="failed">Ошибка</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Box>

        {error && (
          <Box sx={{ mb: 2 }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          {tabValue === 0 ? renderEmailSentTable() : renderTelegramSentTable()}
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Строк на странице:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} из ${count}`}
          />
        </Paper>
      </Box>
  );
};

export default AdvertisementSendResults;
